import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Compute letter-spacing for a monospace line so that exactly `charactersPerLine`
 * characters occupy `targetTextWidthPx` width. Also returns the *actual* per‑character
 * advance measured after applying letter-spacing.
 *
 * Returns: { letterSpacingPx, charAdvancePx }
 */
export default function useMonospaceAdvance({
  wrapperRef,
  sampleText = "A",
  lineSelectorClass, // e.g., styles.fwLine
  charactersPerLine,
  targetTextWidthPx,
}) {
  const [letterSpacingPx, setLetterSpacingPx] = useState(null);
  const [charAdvancePx, setCharAdvancePx] = useState(
    targetTextWidthPx / Math.max(1, charactersPerLine)
  );

  const desiredAdvanceRef = useRef(
    targetTextWidthPx / Math.max(1, charactersPerLine)
  );

  // Measure intrinsic glyph width (no letter-spacing) and solve for needed LS.
  const measureAndSolveLetterSpacing = () => {
    // Infer font-size from a rendered line (if present)
    const anyLine =
      wrapperRef.current?.querySelector?.(`.${lineSelectorClass}`) ?? null;
    let fontSize = "16px";
    if (anyLine) {
      const cs = window.getComputedStyle(anyLine);
      fontSize = cs.fontSize || fontSize;
    }

    // Measure the intrinsic glyph width for sample text
    const probe = document.createElement("span");
    probe.textContent = sampleText || "A";
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.whiteSpace = "pre";
    probe.style.letterSpacing = "0px";
    probe.style.fontFamily =
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
    probe.style.fontWeight = "700";
    probe.style.fontSize = fontSize;
    probe.style.lineHeight = "1em";
    document.body.appendChild(probe);
    const glyphWidthTotal = probe.getBoundingClientRect().width;
    document.body.removeChild(probe);

    const glyphPerChar =
      glyphWidthTotal / Math.max(1, (sampleText || "A").length);
    const desiredAdvance = targetTextWidthPx / Math.max(1, charactersPerLine);
    desiredAdvanceRef.current = desiredAdvance;

    const solvedLS = desiredAdvance - glyphPerChar;
    setLetterSpacingPx(solvedLS);
  };

  useLayoutEffect(() => {
    // Initial solve + update on resize
    const run = () => requestAnimationFrame(measureAndSolveLetterSpacing);
    run();
    const onResize = () => run();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleText, charactersPerLine, targetTextWidthPx, lineSelectorClass]);

  // After we apply letter-spacing in the DOM, measure actual per-char advance from a line
  useEffect(() => {
    if (!wrapperRef.current || letterSpacingPx == null) return;
    const el =
      wrapperRef.current.querySelector?.(`.${lineSelectorClass}`) ?? null;
    if (!el) return;

    requestAnimationFrame(() => {
      const len = Math.max(1, el.textContent.length || 1);
      const w = el.getBoundingClientRect().width;
      const adv = w / len;
      desiredAdvanceRef.current = adv;
      setCharAdvancePx(adv);
    });
  }, [letterSpacingPx, sampleText, lineSelectorClass, wrapperRef]);

  return { letterSpacingPx, charAdvancePx };
}
